import { flash, SubmitButton } from "@xola/ui-kit";
import { nullableNumber, requiredNumberMoreThanZero, requiredString } from "common";
import { Form } from "formik";
import pluralize from "pluralize";
import React from "react";
import { useMutation } from "react-query";
import {
    AggregatorObject,
    AllowedPrivacy,
    BaseSchedule,
    BaseScheduleType,
    InScheduleFilter,
    Membership,
    MembershipUsageType,
    PurchaseRule,
    PurchaseRuleFilterObject,
    PurchaseRuleFilterOperator,
    RuleFilterItem,
    SelectionState,
    ValidationLevel,
} from "types";
import { array, boolean, number, object, string } from "yup";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { MembershipExpiryRestriction } from "@/components/Product/Membership/MembershipRestrictions/MembershipExpiryRestriction";
import {
    MembershipEventTypeRestriction,
    MembershipProductRestriction,
} from "@/components/Product/Membership/MembershipRestrictions/MembershipProducts";
import { RedemptionRuleTransformer } from "@/components/Product/Membership/MembershipRestrictions/MembershipRedemptionRule.transformer";
import { MembershipScheduleInput } from "@/components/Product/Membership/MembershipRestrictions/MembershipScheduleInput";
import {
    MembershipOrganizerRestriction,
    MembershipUsageRestriction,
} from "@/components/Product/Membership/MembershipRestrictions/MembershipUsageLimitations";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useInvalidate } from "@/hooks/useInvalidation";

interface MembershipRestrictionsProps {
    membership: Membership;
}

export interface MembershipUsageLimit {
    usageType: MembershipUsageType;
    usageLimit: number;
}

export interface MembershipRestrictionsFormData extends MembershipUsageLimit {
    purchaseWindowSchedules: BaseSchedule[];
    purchaseWindowBlackoutSchedules: BaseSchedule[];
    arrivalWindowSchedules: BaseSchedule[];
    arrivalWindowBlackoutSchedules: BaseSchedule[];
    expiryInterval: number | null;
    experiences: SelectionState;
    allowedPrivacies: AllowedPrivacy[];
    organizerRequired: boolean;
}

const defaultValues: MembershipRestrictionsFormData = {
    purchaseWindowSchedules: [],
    purchaseWindowBlackoutSchedules: [],
    arrivalWindowSchedules: [],
    arrivalWindowBlackoutSchedules: [],
    expiryInterval: null,
    experiences: { all: true, selected: [] },
    allowedPrivacies: [AllowedPrivacy.PRIVATE, AllowedPrivacy.PUBLIC],
    usageType: MembershipUsageType.UNLIMITED,
    usageLimit: 0,
    organizerRequired: false,
};

export const MembershipRestrictions = ({ membership }: MembershipRestrictionsProps) => {
    const invalidate = useInvalidate();

    const { mutateAsync: savePurchaseRule } = useMutation(async (payload: PurchaseRule) => {
        return api.put(`/purchaseRules/${membership.redemptionPurchaseRule!.id}`, payload);
    });

    const handleSubmit = async (values: MembershipRestrictionsFormData) => {
        await savePurchaseRule({
            ...membership.redemptionPurchaseRule!,
            filter: getPurchaseRuleFilter(values),
        });

        invalidate("products");
        invalidate("memberships", membership.id);
        invalidate("purchaseRuleLinks", membership?.redemptionPurchaseRuleLink?.id);
        invalidate("purchaseRules", membership.redemptionPurchaseRule?.id);

        flash.success("Successfully updated membership restrictions");
    };

    return (
        <ContentLayout isWide>
            <XolaFormik<MembershipRestrictionsFormData>
                enableReinitialize
                enablePrompt
                validationSchema={validationSchema}
                initialValues={getInitialValues(membership)}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, isSubmitting, dirty, handleReset, handleSubmit }) => (
                    <Form className="divide-y divide-gray-light">
                        <ContentLayout.Section
                            title="Purchase Window"
                            description="Limits the dates within which this code can be applied to a booking"
                        >
                            <div data-testid="membership-schedule-editor-restriction-type">
                                <MembershipScheduleInput
                                    title="Purchase Window"
                                    schedules={values.purchaseWindowSchedules}
                                    description="Define the window of time within which a purchase must be made in order to qualify for the discount. Note, that this is independent of the date of the event when the service will be provided."
                                    onChange={(schedules) => setFieldValue("purchaseWindowSchedules", schedules)}
                                />
                                <MembershipScheduleInput
                                    title="Blackout Restrictions"
                                    type={BaseScheduleType.UNAVAILABLE}
                                    schedules={values.purchaseWindowBlackoutSchedules}
                                    description="Define the window of time within which a purchase cannot be made in order to qualify for the discount. Note, that this is independent of the date of the event when the service will be provided."
                                    onChange={(schedules) =>
                                        setFieldValue("purchaseWindowBlackoutSchedules", schedules)
                                    }
                                />
                            </div>
                        </ContentLayout.Section>

                        <ContentLayout.Section
                            title="Arrival Window"
                            description="Limits the start date and start time of the events that this code can be applied to"
                        >
                            <div data-testid="membership-schedule-editor-restriction-type">
                                <MembershipScheduleInput
                                    title="Arrival Window"
                                    schedules={values.arrivalWindowSchedules}
                                    description="Define the window of time within which the event will take place and the services provided."
                                    onChange={(schedules) => setFieldValue("arrivalWindowSchedules", schedules)}
                                />
                                <MembershipScheduleInput
                                    title="Blackout Restrictions"
                                    type={BaseScheduleType.UNAVAILABLE}
                                    schedules={values.arrivalWindowBlackoutSchedules}
                                    description="Define the window of time within which the event cannot take place."
                                    onChange={(schedules) => setFieldValue("arrivalWindowBlackoutSchedules", schedules)}
                                />

                                <MembershipExpiryRestriction />
                            </div>
                        </ContentLayout.Section>

                        <ContentLayout.Section
                            title="Products"
                            description="Select if you would like this coupon to be applicable to all listings, or select individual ones"
                        >
                            <div className="space-y-4">
                                <MembershipProductRestriction />
                                <MembershipEventTypeRestriction />
                            </div>
                        </ContentLayout.Section>

                        <ContentLayout.Section
                            title="Usage Limitations"
                            description="Select if you would like to limit the number of reservations that can be made with the membership pass"
                        >
                            <div className="space-y-4">
                                <MembershipUsageRestriction />
                                <MembershipOrganizerRestriction />
                            </div>
                        </ContentLayout.Section>

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton isLoading={isSubmitting} disabled={!dirty} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" disabled={!dirty} onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </Form>
                )}
            </XolaFormik>
        </ContentLayout>
    );
};

const validationSchema = object().shape({
    purchaseWindowSchedules: array(),
    purchaseWindowBlackoutSchedules: array(),
    arrivalWindowSchedules: array(),
    arrivalWindowBlackoutSchedules: array(),
    expiryInterval: nullableNumber().moreThan(0),
    experiences: object()
        .shape({
            all: boolean(),
            selected: array().of(string()),
        })
        .required(),
    allowedPrivacies: array()
        .of(requiredString().oneOf([AllowedPrivacy.PRIVATE, AllowedPrivacy.PUBLIC]))
        .min(1, "are not selected"),
    usageType: requiredString().oneOf([MembershipUsageType.LIMITED, MembershipUsageType.UNLIMITED]),
    usageLimit: number().when("usageType", {
        is: MembershipUsageType.LIMITED,
        then: requiredNumberMoreThanZero(),
    }),
    organizerRequired: boolean(),
});

const getInitialValues = (membership: Membership): MembershipRestrictionsFormData => {
    if (!membership?.redemptionPurchaseRule?.filter) {
        return defaultValues;
    }

    const purchaseRule = membership.redemptionPurchaseRule;

    return {
        expiryInterval: RedemptionRuleTransformer.getExpiry(purchaseRule),
        experiences: RedemptionRuleTransformer.getExperiences(purchaseRule) ?? { all: true, selected: [] },
        allowedPrivacies: RedemptionRuleTransformer.getAllowedPrivacies(purchaseRule) ?? [
            AllowedPrivacy.PRIVATE,
            AllowedPrivacy.PUBLIC,
        ],
        organizerRequired: RedemptionRuleTransformer.isOrganizerRequired(purchaseRule),
        ...getScheduleAndBlackoutRestrictions(purchaseRule),
        ...RedemptionRuleTransformer.getUsage(purchaseRule),
    };
};

export const membershipRestrictionFields = {
    purchaseWindow: "currentTime",
    arrivalWindow: "arrivalDateTime",
    organizer: "organizer.email",
    privacy: "privacy",
    products: "product.id",
};

/** Transforms restrictions form data to purchase rule filters */
const getPurchaseRuleFilter = (values: MembershipRestrictionsFormData): PurchaseRule["filter"] => {
    const filters: RuleFilterItem[] = [];

    if (values.purchaseWindowSchedules.length > 0) {
        filters.push(inScheduleFilter(values.purchaseWindowSchedules, membershipRestrictionFields.purchaseWindow));
    }

    if (values.purchaseWindowBlackoutSchedules.length > 0) {
        filters.push(
            inScheduleFilter(values.purchaseWindowBlackoutSchedules, membershipRestrictionFields.purchaseWindow),
        );
    }

    if (values.arrivalWindowSchedules.length > 0) {
        filters.push(inScheduleFilter(values.arrivalWindowSchedules, membershipRestrictionFields.arrivalWindow));
    }

    if (values.arrivalWindowBlackoutSchedules.length > 0) {
        filters.push(
            inScheduleFilter(values.arrivalWindowBlackoutSchedules, membershipRestrictionFields.arrivalWindow),
        );
    }

    if (!values.experiences.all) {
        filters.push({
            object: PurchaseRuleFilterObject.IN_FILTER,
            operator: "in",
            field: membershipRestrictionFields.products,
            operand: values.experiences.selected,
            validationLevel: ValidationLevel.ERROR,
            errorMessage: "Product is not supported for this membership",
        });
    }

    if (values.allowedPrivacies.length === 1) {
        filters.push({
            object: PurchaseRuleFilterObject.IN_FILTER,
            operator: "in",
            field: membershipRestrictionFields.privacy,
            operand: values.allowedPrivacies,
            validationLevel: ValidationLevel.ERROR,
            errorMessage: `Event privacy must be ${values.allowedPrivacies.toString()}`,
        });
    }

    if (values.expiryInterval) {
        const expiryDays = Math.round(values.expiryInterval / 60 / 24);
        const errorMessage = `Arrival date for event must be within ${expiryDays} ${pluralize(
            "day",
            expiryDays,
        )} of membership purchase`;

        filters.push({
            object: PurchaseRuleFilterObject.LESSER_THAN_FILTER,
            operator: PurchaseRuleFilterOperator.LT,
            aggregator: {
                object: AggregatorObject.DIFFERENCE_AGGREGATOR,
                operands: [
                    {
                        object: AggregatorObject.RETURN_FIELD_AGGREGATOR,
                        operand: "arrivalDateTime.timestamp",
                    },
                    {
                        object: AggregatorObject.RETURN_FIELD_AGGREGATOR,
                        operand: "membershipPurchaseItem.createdAt.timestamp",
                    },
                ],
            },
            operand: values.expiryInterval * 60,
            validationLevel: ValidationLevel.ERROR,
            errorMessage: errorMessage,
        });
    }

    if (values.organizerRequired) {
        filters.push({
            object: PurchaseRuleFilterObject.EQUALS_FILTER,
            field: membershipRestrictionFields.organizer,
            operand: "",
            operandAggregator: {
                object: AggregatorObject.RETURN_FIELD_AGGREGATOR,
                operand: "membershipPurchaseItem.organizer.email",
            },
            validationLevel: ValidationLevel.ERROR,
            errorMessage: "Holder of the pass must be present to use benefits",
        });
    }

    if (values.usageType === MembershipUsageType.LIMITED && values.usageLimit) {
        filters.push({
            object: PurchaseRuleFilterObject.OR_FILTER,
            operator: "or",
            validationLevel: ValidationLevel.ERROR,
            filters: [
                {
                    object: PurchaseRuleFilterObject.LESSER_THAN_FILTER,
                    field: "appliedMembershipCouponCode.uses",
                    operand: values.usageLimit,
                    operator: PurchaseRuleFilterOperator.LT,
                    validationLevel: ValidationLevel.ERROR,
                    errorMessage: `Membership code can be used for maximum ${values.usageLimit} times`,
                },
                {
                    object: PurchaseRuleFilterObject.IN_FILTER,
                    field: "id",
                    operator: PurchaseRuleFilterOperator.IN,
                    operand: [],
                    operandAggregator: {
                        object: AggregatorObject.RETURN_FIELD_AGGREGATOR,
                        operand: "appliedMembershipCouponCode.objectIds",
                    },
                    validationLevel: ValidationLevel.ERROR,
                },
            ],
        });
    }

    if (filters.length === 0) {
        return null;
    }

    return {
        object: PurchaseRuleFilterObject.AND_FILTER,
        operator: "and",
        filters,
    };
};

const inScheduleFilter = (schedules: BaseSchedule[], field: string): InScheduleFilter => ({
    object: PurchaseRuleFilterObject.IN_SCHEDULE_FILTER,
    operator: "in_schedule",
    schedules: schedules,
    field: field,
    validationLevel: ValidationLevel.ERROR,
});

export const getScheduleAndBlackoutRestrictions = (
    purchaseRule: PurchaseRule,
): Pick<
    MembershipRestrictionsFormData,
    | "purchaseWindowSchedules"
    | "purchaseWindowBlackoutSchedules"
    | "arrivalWindowSchedules"
    | "arrivalWindowBlackoutSchedules"
> => {
    return {
        purchaseWindowSchedules: RedemptionRuleTransformer.getSchedules(
            purchaseRule,
            BaseScheduleType.AVAILABLE,
            "currentTime",
        ),
        purchaseWindowBlackoutSchedules: RedemptionRuleTransformer.getSchedules(
            purchaseRule,
            BaseScheduleType.UNAVAILABLE,
            "currentTime",
        ),
        arrivalWindowSchedules: RedemptionRuleTransformer.getSchedules(
            purchaseRule,
            BaseScheduleType.AVAILABLE,
            "arrivalDateTime",
        ),
        arrivalWindowBlackoutSchedules: RedemptionRuleTransformer.getSchedules(
            purchaseRule,
            BaseScheduleType.UNAVAILABLE,
            "arrivalDateTime",
        ),
    };
};
